import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { vendorPortalApi, type VendorAward } from '../../features/vendorPortal/api.ts';
import { Icon } from '../../components/Icon.tsx';
import { t } from '../../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../../components/Pagination.tsx';

const PAGE_SIZE = 10;

function fmtMvr(n: number): string {
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type PaidFilter = 'all' | 'paid' | 'unpaid';

export function VendorAwards(): JSX.Element {
  const { data: awards = [], isLoading, isError } = useQuery({
    queryKey: ['vendor-awards'],
    queryFn: () => vendorPortalApi.awards(),
  });

  const [paidFilter, setPaidFilter] = useState<PaidFilter>('all');

  // Title/ref search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [paidFilter, debouncedSearch]);

  const filtered = useMemo(() => awards.filter((a) => {
    if (paidFilter === 'paid' && !a.paid) return false;
    if (paidFilter === 'unpaid' && a.paid) return false;
    if (debouncedSearch) {
      const hay = `${a.title} ${a.refNumber}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [awards, paidFilter, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const paidTabs: { key: PaidFilter; label: string }[] = [
    { key: 'all', label: t.vendorPortal.filterAll },
    { key: 'unpaid', label: t.vendorPortal.unpaid },
    { key: 'paid', label: t.vendorPortal.paid },
  ];

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-serif text-3xl tracking-tight">{t.vendorPortal.awardsTitle}</h1>
      </header>

      {/* Toolbar — paid/unpaid filter pills on one side, search on the other. Mirrors Payments.tsx. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* All / unpaid / paid — M3 segmented button. */}
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={t.vendorPortal.awardsTitle}
          >
            {paidTabs.map((tab, i) => {
              const active = paidFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPaidFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.vendorPortal.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{t.vendorPortal.loading}</p>
      ) : isError ? (
        <p className="card-paper p-6 text-center text-sm text-destructive">{t.vendorPortal.loadError}</p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((a: VendorAward) => (
            <Link
              key={a.requestId}
              to="/vendor/requests/$requestId"
              params={{ requestId: a.requestId }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{a.title}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="mono-meta num" dir="ltr">{a.refNumber}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                    {t.vendorPortal.awardedAt} <span className="num">{formatDate(a.awardedAt)}</span>
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="num tabular font-semibold text-ink">{fmtMvr(a.amount)}</span>
                <span className={a.paid ? 'pill pill-success' : 'pill pill-pending'}>
                  {a.paid ? t.vendorPortal.paid : t.vendorPortal.unpaid}
                </span>
              </div>
            </Link>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.vendorPortal.awardsEmpty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
